import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import SettingsIcon from '@material-ui/icons/Settings';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import { Typography } from '@material-ui/core';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelActions from '@material-ui/core/ExpansionPanelActions';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Grid from '@material-ui/core/Grid';
import AssessmentTwoTone from '@material-ui/icons/AssessmentTwoTone';

import SelectableAvatar from './SelectableAvatar';
import ModifiedTimeChip from '../Chips/ModifiedTimeChip';
import ModelTypeChip from '../Chips/ModelTypeChip';
import Checkbox from "@material-ui/core/Checkbox";
import Link from "../../Link";

const styles = theme => ({
  root: {
    padding: theme.spacing(1),
  },

  listItemContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexGrow: 1,
    alignItems: 'center',
    '& > *': {
      margin: `${theme.spacing(0.5)}px`,
      marginLeft: '0',
    },
  },

  textBox: {
    flex: '1 1',
  },

  text: {
    flex: '1 1',
    paddingRight: theme.spacing(2),
  },

  actions: {
    padding: theme.spacing(1),
  },

  avatar: {
    minWidth: '50px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  subsystemBadge: {
    border: `2px solid ${
      theme.palette.type === 'light' ? '#fff' : theme.palette.grey[900]
    }`,
    backgroundColor: theme.palette.primary.light,
  },

  badge: {
    border: `2px solid ${
      theme.palette.type === 'light' ? '#fff' : theme.palette.grey[900]
    }`,
  },

  icon: {
    marginRight: '6px',
    fontSize: 18,
  },

  link: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  gridContainer: {
    marginRight: theme.spacing(1),
    alignItems: 'center',
  },

  listItemText: {
    [theme.breakpoints.up('md')]: {
      marginRight: '450px',
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: '128px',
    },
  },

  listItemTextOverview: {
    marginRight: '128px',
  },

  timeChip: {
    minWidth: '160px',
    textAlign: 'right',
    marginLeft: '128px',
  },

  centerChildren: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
  },

  modelDetails: {
    marginRight: '128px',
  },

  checkBox: {
    padding: 0,
  }
});

const getVerboseName = (detail) => {
  try {
    const type = Object.keys(detail.type)[0];
    return type === 'Hybrid Causal Logic' ? null : type;
  } catch (e) {
    return null;
  }
};

const SettingsIconButton = (props) => {
  const {
    classes: { link, centerChildren, icon }, detail, basePath, detail: { id },
  } = props;
  return (
    <Link
      className={link}
      to={{
        pathname: `${basePath}/${id}/settings`,
        state: { details: detail },
      }}
    >
      <Tooltip title="Settings">
        <Typography color="textSecondary" className={centerChildren}>
          <SettingsIcon className={icon} />
        </Typography>
      </Tooltip>
    </Link>
  );
};

const ModelEntry = (props) => {
  const {
    classes, detail, detail: { id }, basePath, isOverview, handleChange, selected, isPicker, sortRule
  } = props;

  const verboseName = getVerboseName(detail);
  const results = (
    <Link
      to={{
        pathname: `${basePath}/${detail.id}/quantify`,
        state: { details: detail },
      }}

    >
      <Tooltip title="Results">
        <Typography color="textSecondary" className={classes.centerChildren}>
          <AssessmentTwoTone className={classes.icon} />
        </Typography>
      </Tooltip>
    </Link>
  );

  const checkBox = (
    <Checkbox
      checked={selected}
      color="primary"
      onClick={e => {
        e.stopPropagation();
        handleChange(e);
      }}
      className={classes.checkBox} />
  );

  const instanceEntries =
    detail.instances.map(item => (
      <ListItem
        divider
        button
      >
        <div className={classes.avatar}>
          <ListItemAvatar>
            <SelectableAvatar
              title={item.title}
              id={item.id}
            />
          </ListItemAvatar>
        </div>
        <ListItemText
          primary={item.title}
          secondary={item.description}
          primaryTypographyProps={{
            noWrap: true,
          }}
          secondaryTypographyProps={{
            noWrap: true,
          }}
          className={
            isPicker ? null : isOverview ? classes.listItemTextOverview : classes.listItemText
          }
        />
      </ListItem>
    ));

  const responsiveSecondaryAction = !isOverview ? (
    <Grid container spacing={2} className={classes.gridContainer}>
      <Grid item className={classes.timeChip}>
        <ModifiedTimeChip timestamp={detail.date_modified} sortRule={sortRule} />
      </Grid>
      <Grid item>
        <SettingsIconButton basePath={basePath} detail={detail} classes={classes} />
      </Grid>
      <Grid item>
        {results}
      </Grid>
      <Grid item>
        {checkBox}
      </Grid>
      <Grid item>
        {checkBox}
      </Grid>
    </Grid>
  ) : (
    <Grid container spacing={16} className={classes.gridContainer}>
      <Grid item className={classes.timeChip}>
        <ModifiedTimeChip timestamp={detail.date_modified} sortRule={sortRule} />
      </Grid>
    </Grid>
  );

  return (
    <React.Fragment>
      <Link
        to={{
          pathname: `${basePath}/${id}`,
          state: { details: detail },
        }}
        onClick={(e => isPicker && e.preventDefault())}
      >
        <ListItem
          divider
          button
          selected={selected}
          onClick={handleChange}
        >
          <div className={classes.avatar}>
            <ListItemAvatar>
              <SelectableAvatar
                selected={!isPicker && selected}
                onChange={handleChange}
                title={detail.title}
                id={detail.id}
              />
            </ListItemAvatar>
            <ModelTypeChip type={verboseName} />
          </div>
          <ListItemText
            primary={detail.title}
            secondary={detail.description}
            primaryTypographyProps={{
              noWrap: true,
            }}
            secondaryTypographyProps={{
              noWrap: true,
            }}
            className={
              isPicker ? null : isOverview ? classes.listItemTextOverview : classes.listItemText
            }
          />
          <ListItemSecondaryAction>{!isPicker && responsiveSecondaryAction}</ListItemSecondaryAction>
        </ListItem>
      </Link>
      {
        (detail.instances) && (detail.instances.length > 0) &&
        <List subheader={<ListSubheader>Instances</ListSubheader>}>
          {instanceEntries}
        </List>
      }

    </React.Fragment>
  );
};

ModelEntry.defaultProps = {
  isPicker: false,
  isOverview: false,
  basePath: '/model',
};

const InstanceEntryPropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  source: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  date_created: PropTypes.string,
  date_modified: PropTypes.string,
  creator: PropTypes.number,
  assigned_users: PropTypes.array,
  description: PropTypes.string,
});

ModelEntry.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    date_created: PropTypes.string,
    date_modified: PropTypes.string,
    creator: PropTypes.number,
    assigned_users: PropTypes.array,
    description: PropTypes.string,
    path: PropTypes.string,
    type: PropTypes.instanceOf(Object),
    subsystems: PropTypes.array,
    instances: PropTypes.arrayOf(InstanceEntryPropTypes).isRequired,
  }).isRequired,
  isOverview: PropTypes.bool,
  classes: PropTypes.instanceOf(Object).isRequired,
  selected: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  isPicker: PropTypes.bool,
  basePath: PropTypes.string,
  sortRule: PropTypes.string,
};

ModelEntry.defaultProps = {
  sortRule: ''
};

export default withStyles(styles)(withRouter((ModelEntry)));

//
// import React from 'react';
// import PropTypes from 'prop-types';
// import { withRouter } from 'react-router-dom';
// import { withStyles } from '@material-ui/core/styles';
// import ListItem from '@material-ui/core/ListItem';
// import ListItemAvatar from '@material-ui/core/ListItemAvatar';
// import ListItemText from '@material-ui/core/ListItemText';
// import Tooltip from '@material-ui/core/Tooltip';
// import SettingsIcon from '@material-ui/icons/Settings';
// import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
// import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
// import { Typography } from '@material-ui/core';
// import ExpansionPanel from '@material-ui/core/ExpansionPanel';
// import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
// import ExpansionPanelActions from '@material-ui/core/ExpansionPanelActions';
// import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
// import Grid from '@material-ui/core/Grid';
// import AssessmentTwoTone from '@material-ui/icons/AssessmentTwoTone';
//
// import SelectableAvatar from './SelectableAvatar';
// import ModifiedTimeChip from '../Chips/ModifiedTimeChip';
// import ModelTypeChip from '../Chips/ModelTypeChip';
// import Checkbox from "@material-ui/core/Checkbox";
// import Link from "../../Link";
//
// const styles = theme => ({
//   root: {
//     padding: theme.spacing(1),
//   },
//
//   listItemContainer: {
//     display: 'flex',
//     flexWrap: 'wrap',
//     flexGrow: 1,
//     alignItems: 'center',
//     '& > *': {
//       margin: `${theme.spacing(0.5)}px`,
//       marginLeft: '0',
//     },
//   },
//
//   textBox: {
//     flex: '1 1',
//   },
//
//   text: {
//     flex: '1 1',
//     paddingRight: theme.spacing(2),
//   },
//
//   actions: {
//     padding: theme.spacing(1),
//   },
//
//   avatar: {
//     minWidth: '50px',
//     textAlign: 'center',
//     display: 'flex',
//     flexDirection: 'column',
//     alignItems: 'center',
//     justifyContent: 'center',
//   },
//
//   subsystemBadge: {
//     border: `2px solid ${
//       theme.palette.type === 'light' ? '#fff' : theme.palette.grey[900]
//     }`,
//     backgroundColor: theme.palette.primary.light,
//   },
//
//   badge: {
//     border: `2px solid ${
//       theme.palette.type === 'light' ? '#fff' : theme.palette.grey[900]
//     }`,
//   },
//
//   icon: {
//     marginRight: '6px',
//     fontSize: 18,
//   },
//
//   link: {
//     '&:hover': {
//       textDecoration: 'underline',
//     },
//   },
//
//   gridContainer: {
//     marginRight: theme.spacing(1),
//     alignItems: 'center',
//   },
//
//   listItemText: {
//     [theme.breakpoints.up('md')]: {
//       marginRight: '450px',
//     },
//     [theme.breakpoints.down('sm')]: {
//       marginRight: '128px',
//     },
//   },
//
//   listItemTextOverview: {
//     marginRight: '128px',
//   },
//
//   timeChip: {
//     minWidth: '160px',
//     textAlign: 'right',
//     marginLeft: '128px',
//   },
//
//   centerChildren: {
//     display: 'flex',
//     alignContent: 'center',
//     justifyContent: 'center',
//   },
//
//   modelDetails: {
//     marginRight: '128px',
//   },
//
//   checkBox: {
//     padding: 0,
//   }
// });
//
// const getVerboseName = (detail) => {
//   try {
//     const type = Object.keys(detail.type)[0];
//     return type === 'Hybrid Causal Logic' ? null : type;
//   } catch (e) {
//     return null;
//   }
// };
//
// const SettingsIconButton = (props) => {
//   const {
//     classes: { link, centerChildren, icon }, detail, basePath, detail: { id },
//   } = props;
//   return (
//     <Link
//       className={link}
//       to={{
//         pathname: `${basePath}/${id}/settings`,
//         state: { details: detail },
//       }}
//     >
//       <Tooltip title="Settings">
//         <Typography color="textSecondary" className={centerChildren}>
//           <SettingsIcon className={icon} />
//         </Typography>
//       </Tooltip>
//     </Link>
//   );
// };
//
// const ModelEntry = (props) => {
//   const {
//     classes, detail, detail: { id }, basePath, isOverview, handleChange, selected, isPicker, sortRule
//   } = props;
//
//   const verboseName = getVerboseName(detail);
//   const results = (
//     <Link
//       to={{
//         pathname: `${basePath}/${detail.id}/quantify`,
//         state: { details: detail },
//       }}
//
//     >
//       <Tooltip title="Results">
//         <Typography color="textSecondary" className={classes.centerChildren}>
//           <AssessmentTwoTone className={classes.icon} />
//         </Typography>
//       </Tooltip>
//     </Link>
//   );
//
//   const checkBox = (
//     <Checkbox
//       checked={selected}
//       color="primary"
//       onClick={e => {
//         e.stopPropagation();
//         handleChange(e);
//       }}
//       className={classes.checkBox} />
//   );
//
//   const responsiveSecondaryAction = !isOverview ? (
//     <Grid container spacing={2} className={classes.gridContainer}>
//       <Grid item className={classes.timeChip}>
//         <ModifiedTimeChip timestamp={detail.date_modified} sortRule={sortRule} />
//       </Grid>
//       <Grid item>
//         <SettingsIconButton basePath={basePath} detail={detail} classes={classes} />
//       </Grid>
//       <Grid item>
//         {results}
//       </Grid>
//       <Grid item>
//         {checkBox}
//       </Grid>
//       <Grid item>
//         {checkBox}
//       </Grid>
//     </Grid>
//   ) : (
//     <Grid container spacing={16} className={classes.gridContainer}>
//       <Grid item className={classes.timeChip}>
//         <ModifiedTimeChip timestamp={detail.date_modified} sortRule={sortRule} />
//       </Grid>
//     </Grid>
//   );
//
//   return (
//     <Link
//       to={{
//         pathname: `${basePath}/${id}`,
//         state: { details: detail },
//       }}
//       onClick={(e => isPicker && e.preventDefault())}
//     >
//       <ExpansionPanel expanded>
//         <ExpansionPanelSummary>
//           <div className={classes.avatar}>
//             <ListItemAvatar>
//               <SelectableAvatar
//                 selected={!isPicker && selected}
//                 onChange={handleChange}
//                 title={detail.title}
//                 id={detail.id}
//               />
//             </ListItemAvatar>
//             <ModelTypeChip type={verboseName} />
//           </div>
//           <ListItemText
//             primary={detail.title}
//             secondary={detail.description}
//             primaryTypographyProps={{
//               noWrap: true,
//             }}
//             secondaryTypographyProps={{
//               noWrap: true,
//             }}
//             className={
//               isPicker ? null : isOverview ? classes.listItemTextOverview : classes.listItemText
//             }
//           />
//           <ListItemSecondaryAction>{!isPicker && responsiveSecondaryAction}</ListItemSecondaryAction>
//         </ExpansionPanelSummary>
//         <ExpansionPanelDetails>
//           <ListItem
//             divider
//             button
//             className={classes.listItemContainer}
//             selected={selected}
//             onClick={handleChange}
//           >
//             <div className={classes.avatar}>
//               <ListItemAvatar>
//                 <SelectableAvatar
//                   selected={!isPicker && selected}
//                   onChange={handleChange}
//                   title={detail.title}
//                   id={detail.id}
//                 />
//               </ListItemAvatar>
//               <ModelTypeChip type={verboseName} />
//             </div>
//             <ListItemText
//               primary={detail.title}
//               secondary={detail.description}
//               primaryTypographyProps={{
//                 noWrap: true,
//               }}
//               secondaryTypographyProps={{
//                 noWrap: true,
//               }}
//               className={
//                 isPicker ? null : isOverview ? classes.listItemTextOverview : classes.listItemText
//               }
//             />
//             <ListItemSecondaryAction>{!isPicker && responsiveSecondaryAction}</ListItemSecondaryAction>
//           </ListItem>
//         </ExpansionPanelDetails>
//       </ExpansionPanel>
//     </Link>
//   );
// };
//
// ModelEntry.defaultProps = {
//   isPicker: false,
//   isOverview: false,
//   basePath: '/model',
// };
//
// const InstanceEntryPropTypes = PropTypes.shape({
//   id: PropTypes.number.isRequired,
//   source: PropTypes.number.isRequired,
//   title: PropTypes.string.isRequired,
//   date_created: PropTypes.string,
//   date_modified: PropTypes.string,
//   creator: PropTypes.number,
//   assigned_users: PropTypes.array,
//   description: PropTypes.string,
// });
//
// ModelEntry.propTypes = {
//   detail: PropTypes.shape({
//     id: PropTypes.number,
//     title: PropTypes.string,
//     date_created: PropTypes.string,
//     date_modified: PropTypes.string,
//     creator: PropTypes.number,
//     assigned_users: PropTypes.array,
//     description: PropTypes.string,
//     path: PropTypes.string,
//     type: PropTypes.instanceOf(Object),
//     subsystems: PropTypes.array,
//     instances: PropTypes.arrayOf(InstanceEntryPropTypes).isRequired,
//   }).isRequired,
//   isOverview: PropTypes.bool,
//   classes: PropTypes.instanceOf(Object).isRequired,
//   selected: PropTypes.bool.isRequired,
//   handleChange: PropTypes.func.isRequired,
//   isPicker: PropTypes.bool,
//   basePath: PropTypes.string,
//   sortRule: PropTypes.string,
// };
//
// ModelEntry.defaultProps = {
//   sortRule: ''
// };
//
// export default withStyles(styles)(withRouter((ModelEntry)));
//
//
